import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from './baseQuery';

export interface IOfferwallCase {
  id: number;
  url: string;
  icon_url: string;
  title: string;
  requirements: string;
  point: string | null;
  os: number | null;
  expire_at: string | null;
  stages?: string | null;
  deleted_at?: string | null;
  bonus_point?: string | number | null;
  notes?: string | null;
  title_internal?: string | null;
  clid?: string | null;
  type?: string | null;
  sort?: string | number | null;
  bonus_type?: string | number | null;
  category?: string | null;
  flag?: number | null;
  threshold?: number | null;
}

export interface IOfferwallRecordPayload {
  url: string;
  icon_url: string;
  title: string;
  requirements: string;
  point?: string | null;
  os?: string | number | null;
  expire_at?: string | null;
  deleted_at?: string | null;
  stages?: string | null;
  bonus_point?: string | number | null;
  notes?: string | null;
  event_id?: string | number | null;
  max_point?: string | number | null;
  title_internal?: string | null;
  clid?: string | null;
  type?: string | null;
  sort?: string | number | null;
  bonus_type?: string | number | null;
  category?: string | null;
}

export const offerwallApi = createApi({
  reducerPath: 'offerwallApi',
  tagTypes: ['offerwall'],
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    submit: builder.mutation<void, IOfferwallRecordPayload>({
      query: (body) => ({
        url: '/offerwall_ad_add',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['offerwall'],
    }),

    updateAd: builder.mutation<void, { id: number; payload: IOfferwallRecordPayload }>({
      query: ({ id, payload }) => ({
        url: '/offerwall_update_ad',
        method: 'POST',
        data: {
          id,
          ...payload,
        },
      }),
      invalidatesTags: ['offerwall'],
    }),

    submitDeleteUrl: builder.mutation<void, { delete_url: string}>({
      query: (body) => ({
        url: '/offerwall_delete_ad',
        method: 'POST',
        data: {
          url: body.delete_url
        }
      }),
    }),

    submitSuspend: builder.mutation<void, {reportId: number, suspend: number }>({
      query: (data) => ({
        url: '/offerwall_suspend',
        method: 'POST',
        data: {
          reportId: data.reportId, // Convert to int
          suspend: data.suspend,
        },
      }),
    }),

    submitFlag: builder.mutation<void, {reportId: number, flag: number }>({
      query: (data) => ({
        url: '/offerwall_flag',
        method: 'POST',
        data: {
          reportId: data.reportId, // Convert to int
          flag: data.flag,
        },
      }),
    }),

    submitThreshold: builder.mutation<void, {reportId: number, threshold: number }>({
      query: (data) => ({
        url: '/offerwall_threshold',
        method: 'POST',
        data: {
          reportId: data.reportId, // Convert to int
          threshold: data.threshold,
        },
      }),
    }),

    // New endpoint for fetching ads
    getAds: builder.query<IOfferwallCase[], void>({
      query: params => ({
        url: '/offerwall_get_ads',
        method: 'GET',
      }),
      providesTags: ['offerwall'],
      transformResponse: (response: IOfferwallCase[] | { data?: IOfferwallCase[] }) =>
        Array.isArray(response) ? response : response.data || [],
    }),

  }),
});

// Export both mutation hooks
export const {
  useSubmitMutation,
  useSubmitDeleteUrlMutation,
  useSubmitSuspendMutation,
  useSubmitFlagMutation,
  useSubmitThresholdMutation,
  useUpdateAdMutation,
  useGetAdsQuery,
} = offerwallApi;
